
class OwlCarouselElement extends HTMLElement {
  constructor() {
    super();
    this.owlInstance = null;
    this.resizeHandler = null;
    this.navHandlers = [];
    this.navSyncHandler = null;
    this.dragStopHandler = null;
    this.wheelHandler = null;
    this.wheelTarget = null;
    this.initTimer = null;
    this.readyToInit = true;
  }

  connectedCallback() {
    this.originalItems = this.getCarousel()?.children.length || 0;

    const initDelay = Number(this.getAttribute("data-init-delay") || 0);
    if (initDelay > 0) {
      this.readyToInit = false;
      this.initTimer = setTimeout(() => {
        this.readyToInit = true;
        this.init();
      }, initDelay);
    } else {
      this.init();
    }

    this.setupResizeListener();
  }

  disconnectedCallback() {
    if (this.initTimer) {
      clearTimeout(this.initTimer);
      this.initTimer = null;
    }
    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler);
    }
    this.destroyOwl();
  }

  init() {
    this.updateOwlState();
  }

  getCarousel() {
    return this.querySelector(".owl-carousel");
  }

  getResponsiveConfig() {
    const attr = this.getAttribute("data-responsive");
    if (!attr) return {};
    try {
      return JSON.parse(attr);
    } catch (e) {
      console.warn("Invalid data-responsive JSON", e);
      return {};
    }
  }

  getCurrentItemsCount() {
    const responsive = this.getResponsiveConfig();
    const width = document.documentElement.clientWidth;
    let items = Number(this.getAttribute("data-items")) || 1;

    Object.keys(responsive).forEach(bp => {
      if (width >= Number(bp) && responsive[bp].items) {
        items = responsive[bp].items;
      }
    });

    return items;
  }

  updateOwlState() {
    const carousel = this.getCarousel();
    if (!carousel || !this.readyToInit) return;

    const totalItems = this.originalItems;
    const visibleItems = this.getCurrentItemsCount();

    if (totalItems > visibleItems) {
      if (!this.owlInstance) this.enableOwl();
    } else {
      if (this.owlInstance) this.destroyOwl();
    }

    this.syncNavState();
  }

  enableOwl() {
    if (this.owlInstance) return;

    const $carousel = $(this.getCarousel());

    const loopEnabled = this.getAttribute("data-loop") === "true";
    const autoplayEnabled = this.getAttribute("data-autoplay") === "true";

    const options = {
      loop: loopEnabled,
      rewind: !loopEnabled && this.getAttribute("data-rewind") === "true",
      dots: this.getAttribute("data-dots") === "true",
      nav: this.getAttribute("data-nav") === "true",
      autoplay: autoplayEnabled,
      autoWidth: false,
      center: this.getAttribute("data-center") === "true",
      autoplayTimeout: Number(
        this.getAttribute("data-autoplay-timeout") || 6000
      ),

      responsive: this.getResponsiveConfig()
    };

    // Only forwarded when declared, so Owl's own defaults still apply otherwise.
    if (this.hasAttribute("data-items")) {
      options.items = Number(this.getAttribute("data-items"));
    }

    if (this.hasAttribute("data-margin")) {
      options.margin = Number(this.getAttribute("data-margin"));
    }

    if (this.hasAttribute("data-slide-by")) {
      options.slideBy = Number(this.getAttribute("data-slide-by"));
    }

    if (this.hasAttribute("data-autoplay-hover-pause")) {
      options.autoplayHoverPause =
        this.getAttribute("data-autoplay-hover-pause") === "true";
    }

    if (this.hasAttribute("data-autoplay-speed")) {
      options.autoplaySpeed = Number(this.getAttribute("data-autoplay-speed"));
    }

    if (this.hasAttribute("data-smart-speed")) {
      options.smartSpeed = Number(this.getAttribute("data-smart-speed"));
    }

    if (this.hasAttribute("data-slide-transition")) {
      options.slideTransition = this.getAttribute("data-slide-transition");
    }

    if (this.hasAttribute("data-autoplay-timeout")) {
      options.autoplayTimeout = Number(
        this.getAttribute("data-autoplay-timeout")
      );
    }
    $carousel.owlCarousel(options);
    this.owlInstance = $carousel;

    this.bindNav($carousel);
    this.applyStateClasses(true);

    this.navSyncHandler = () => this.syncNavState();
    $carousel.on("refreshed.owl.carousel", this.navSyncHandler);
    $carousel.on("resized.owl.carousel", this.navSyncHandler);

 
    if (this.getAttribute("data-autoplay-stop-on-drag") === "true") {
      this.dragStopHandler = () => $carousel.trigger("stop.owl.autoplay");
      $carousel.on("dragged.owl.carousel", this.dragStopHandler);
    }


    // run through the whole gallery.
    if (this.getAttribute("data-mousewheel") === "true") {
      let lastWheelAt = 0;
      this.wheelHandler = event => {
        const delta =
          Math.abs(event.deltaX) > Math.abs(event.deltaY)
            ? event.deltaX
            : event.deltaY;
        if (!delta) return;

        event.preventDefault();

        if (event.timeStamp - lastWheelAt < 300) return;
        lastWheelAt = event.timeStamp;

        $carousel.trigger(
          delta > 0 ? "next.owl.carousel" : "prev.owl.carousel"
        );
      };
      this.wheelTarget = this.getCarousel();
      this.wheelTarget.addEventListener("wheel", this.wheelHandler, {
        passive: false
      });
    }
  }

  setupResizeListener() {
    let resizeTimeout;
    this.resizeHandler = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.updateOwlState();
      }, 200);
    };
    window.addEventListener("resize", this.resizeHandler);
  }

  getNavElement(attribute) {
    const selector = this.getAttribute(attribute);
    return selector ? document.querySelector(selector) : null;
  }

  bindNav($carousel) {
    this.unbindNav();

    [
      ["data-nav-prev", "prev.owl.carousel"],
      ["data-nav-next", "next.owl.carousel"]
    ].forEach(([attribute, event]) => {
      const el = this.getNavElement(attribute);
      if (!el) return;

      const handler = () => $carousel.trigger(event);
      el.addEventListener("click", handler);
      this.navHandlers.push({ el, handler });
    });
  }

  unbindNav() {
    this.navHandlers.forEach(({ el, handler }) =>
      el.removeEventListener("click", handler)
    );
    this.navHandlers = [];
  }

  // Mirrors the old slider's watchOverflow: the arrows read as disabled whenever the carousel
  // is not active, which is exactly when there are too few items to scroll.
  syncNavState() {
    const disabledClass = this.getAttribute("data-nav-disabled-class");
    if (!disabledClass) return;

    const locked = !this.owlInstance;
    ["data-nav-prev", "data-nav-next"].forEach(attribute => {
      const el = this.getNavElement(attribute);
      if (el) el.classList.toggle(disabledClass, locked);
    });
  }

  applyStateClasses(add) {
    const selector = this.getAttribute("data-state-target");
    const classes = (this.getAttribute("data-state-classes") || "")
      .split(/\s+/)
      .filter(Boolean);

    if (!selector || !classes.length) return;

    const target = this.closest(selector) || document.querySelector(selector);
    if (!target) return;

    if (add) {
      target.classList.add(...classes);
    } else {
      target.classList.remove(...classes);
    }
  }

  destroyOwl() {
    if (!this.owlInstance) return;

    this.unbindNav();

    if (this.navSyncHandler) {
      this.owlInstance.off("refreshed.owl.carousel", this.navSyncHandler);
      this.owlInstance.off("resized.owl.carousel", this.navSyncHandler);
      this.navSyncHandler = null;
    }

    if (this.dragStopHandler) {
      this.owlInstance.off("dragged.owl.carousel", this.dragStopHandler);
      this.dragStopHandler = null;
    }

    if (this.wheelHandler && this.wheelTarget) {
      this.wheelTarget.removeEventListener("wheel", this.wheelHandler);
      this.wheelHandler = null;
      this.wheelTarget = null;
    }

    this.owlInstance.trigger("destroy.owl.carousel");
    this.owlInstance.find(".owl-stage-outer").children().unwrap();
    this.owlInstance.removeClass("owl-loaded");
    this.owlInstance = null;

    this.applyStateClasses(false);
  }
}

// Guarded: a second define() for the same tag name throws, and this file is easy to
// include twice while sections are being migrated.
if (!customElements.get("custom-owlcarousel")) {
  customElements.define("custom-owlcarousel", OwlCarouselElement);
}
